import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import {
  FiActivity,
  FiArrowRight,
  FiCheckCircle,
  FiHeart,
  FiShield,
  FiTrendingUp,
  FiUsers,
  FiZap
} from 'react-icons/fi';
import { usePageSEO } from '../hooks/useSEO';

const DIRECTIONS = [
  {
    icon: FiTrendingUp,
    number: '01',
    title: 'Спортивное питание',
    text: 'Протеины, гейнеры, аминокислоты и продукты для восстановления после нагрузки.',
    tone: 'dark'
  },
  {
    icon: FiHeart,
    number: '02',
    title: 'Ежедневный рацион',
    text: 'Травяные напитки, витаминно-минеральные комплексы и продукты на каждый день.',
    tone: 'clay'
  },
  {
    icon: FiUsers,
    number: '03',
    title: 'Подборки экспертов',
    text: 'Готовые сочетания продуктов от тренеров и нутрициологов под конкретную задачу.',
    tone: 'sage'
  }
];

const Page = styled.main`
  --srv-bg: #f5f1e9;
  --srv-ink: #20211e;
  --srv-muted: #676a62;
  --srv-line: #ddd7cc;
  --srv-accent: #b65a3a;
  --srv-sage: #657456;

  overflow: hidden;
  background: var(--srv-bg);
  color: var(--srv-ink);
`;

const Hero = styled.section`
  position: relative;
  width: min(1400px, calc(100% - 32px));
  min-height: 690px;
  margin: 16px auto 0;
  overflow: hidden;
  border-radius: 26px;
  display: flex;
  align-items: center;
  background: #242620;
  color: #fff;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, rgba(20,22,19,.97), rgba(20,22,19,.88) 38%, rgba(20,22,19,.24) 68%, rgba(20,22,19,.04));
  }

  @media (max-width: 760px) {
    width: 100%;
    min-height: 0;
    border-radius: 0;
    display: block;
    &::after { display: none; }
  }
`;

const HeroImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;

  @media (max-width: 760px) {
    position: relative;
    height: 320px;
    object-position: 70% center;
  }
`;

const HeroInner = styled.div`
  position: relative;
  z-index: 2;
  width: min(1240px, calc(100% - 96px));
  margin: 0 auto;
  padding: 88px 0;

  @media (max-width: 760px) { width: 100%; padding: 50px 20px 62px; background: #242620; }
`;

const HeroCopy = styled.div`
  max-width: 720px;

  h1 {
    margin: 0;
    font-family: var(--font-heading, 'BebasNeuePro', sans-serif);
    font-size: clamp(60px, 7vw, 100px);
    line-height: .9;
    letter-spacing: .02em;
    text-transform: uppercase;
  }

  > p { max-width: 620px; margin: 26px 0 0; color: rgba(255,255,255,.76); font-size: 18px; line-height: 1.65; }
`;

const Eyebrow = styled.p`
  margin: 0 0 16px !important;
  color: #e0ad64 !important;
  font-size: 12px !important;
  line-height: 1.2 !important;
  font-weight: 800;
  letter-spacing: .16em;
  text-transform: uppercase;
`;

const Actions = styled.div`
  margin-top: 32px;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;

const Button = styled(Link)`
  min-height: 52px;
  padding: 0 22px;
  border: 1px solid ${props => props.$primary ? '#b65a3a' : 'rgba(255,255,255,.28)'};
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background: ${props => props.$primary ? '#b65a3a' : 'transparent'};
  color: #fff;
  font-size: 14px;
  font-weight: 800;

  &:hover { background: ${props => props.$primary ? '#98472f' : 'rgba(255,255,255,.1)'}; transform: translateY(-2px); }
  &:focus-visible { outline: 3px solid #fff; outline-offset: 3px; }
`;

const ProofBar = styled.div`
  position: relative;
  z-index: 3;
  width: min(1180px, calc(100% - 48px));
  margin: -38px auto 0;
  padding: 24px 28px;
  border: 1px solid rgba(32,33,30,.1);
  border-radius: 16px;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  background: #fff;
  box-shadow: 0 22px 55px rgba(36,32,27,.12);

  div { display: grid; grid-template-columns: 34px 1fr; gap: 12px; align-items: center; }
  svg { width: 23px; height: 23px; color: var(--srv-accent); }
  strong { display: block; font-size: 14px; line-height: 1.35; }
  span { display: block; margin-top: 4px; color: var(--srv-muted); font-size: 11px; line-height: 1.45; }

  @media (max-width: 760px) {
    width: min(100% - 32px, 1180px);
    margin-top: 24px;
    grid-template-columns: 1fr;
  }
`;

const Section = styled.section`
  width: min(1180px, calc(100% - 48px));
  margin: 0 auto;
  padding: clamp(78px, 8vw, 120px) 0;

  @media (max-width: 700px) { width: min(100% - 32px, 1180px); }
`;

const SectionHead = styled.header`
  max-width: 760px;
  margin-bottom: 42px;

  span { color: #8f432d; font-size: 12px; font-weight: 800; letter-spacing: .16em; text-transform: uppercase; }
  h2 { margin: 14px 0 0; font-family: var(--font-heading, 'BebasNeuePro', sans-serif); font-size: clamp(48px, 5.5vw, 76px); line-height: .94; text-transform: uppercase; }
  p { max-width: 680px; margin: 20px 0 0; color: var(--srv-muted); font-size: 17px; line-height: 1.65; }
`;

const DirectionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;

  @media (max-width: 800px) { grid-template-columns: 1fr; }
`;

const DirectionCard = styled.article`
  position: relative;
  min-height: 330px;
  padding: 30px;
  overflow: hidden;
  border: 1px solid ${props => props.$tone === 'dark' ? '#242620' : 'var(--srv-line)'};
  border-radius: 18px;
  background: ${props => props.$tone === 'dark' ? '#242620' : props.$tone === 'clay' ? '#ead9d0' : '#e5eadc'};
  color: ${props => props.$tone === 'dark' ? '#fff' : 'var(--srv-ink)'};

  &::after {
    content: '${props => props.$number}';
    position: absolute;
    top: 6px;
    right: 14px;
    font-family: var(--font-heading, 'BebasNeuePro', sans-serif);
    font-size: 102px;
    line-height: 1;
    opacity: .07;
  }

  svg { width: 28px; height: 28px; color: ${props => props.$tone === 'dark' ? '#e0ad64' : 'var(--srv-accent)'}; }
  h3 { margin: 110px 0 12px; font-family: var(--font-heading, 'BebasNeuePro', sans-serif); font-size: 36px; line-height: 1; text-transform: uppercase; }
  p { margin: 0; color: inherit; font-size: 14px; line-height: 1.6; opacity: .72; }
`;

const Feature = styled.section`
  width: min(1400px, calc(100% - 32px));
  min-height: 640px;
  margin: 0 auto;
  overflow: hidden;
  border-radius: 26px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  background: #fff;

  @media (max-width: 820px) { width: 100%; min-height: 0; border-radius: 0; grid-template-columns: 1fr; }
`;

const FeatureImage = styled.img`
  width: 100%;
  height: 100%;
  min-height: 640px;
  object-fit: cover;
  object-position: 67% center;

  @media (max-width: 820px) { min-height: 340px; }
`;

const FeatureCopy = styled.div`
  padding: clamp(44px, 6vw, 84px);
  display: flex;
  flex-direction: column;
  justify-content: center;

  h2 { margin: 0; font-family: var(--font-heading, 'BebasNeuePro', sans-serif); font-size: clamp(50px, 5.5vw, 78px); line-height: .94; text-transform: uppercase; }
  > p { margin: 24px 0 0; color: var(--srv-muted); font-size: 16px; line-height: 1.65; }
`;

const Checklist = styled.div`
  margin-top: 28px;
  display: grid;
  gap: 12px;

  div { display: grid; grid-template-columns: 24px 1fr; gap: 11px; align-items: start; color: #4f534b; font-size: 14px; line-height: 1.55; }
  svg { margin-top: 2px; color: var(--srv-sage); }
`;

const Taste = styled.section`
  position: relative;
  width: min(1400px, calc(100% - 32px));
  min-height: 680px;
  margin: clamp(78px, 8vw, 120px) auto 0;
  overflow: hidden;
  border-radius: 26px;
  display: flex;
  align-items: center;
  background: #1f1714;
  color: #fff;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, rgba(29,20,17,.98), rgba(29,20,17,.9) 40%, rgba(29,20,17,.24) 70%, rgba(29,20,17,.04));
  }

  @media (max-width: 760px) { width: 100%; min-height: 0; border-radius: 0; display: block; &::after { display: none; } }
`;

const TasteImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;

  @media (max-width: 760px) { position: relative; height: 320px; object-position: 70% center; }
`;

const TasteInner = styled.div`
  position: relative;
  z-index: 2;
  width: min(1180px, calc(100% - 96px));
  margin: 0 auto;
  padding: 82px 0;

  @media (max-width: 760px) { width: 100%; padding: 50px 20px 62px; background: #1f1714; }
`;

const TasteCopy = styled.div`
  max-width: 650px;

  h2 { margin: 0; font-family: var(--font-heading, 'BebasNeuePro', sans-serif); font-size: clamp(54px, 6vw, 86px); line-height: .92; text-transform: uppercase; }
  > p { max-width: 570px; margin: 24px 0 0; color: rgba(255,255,255,.75); font-size: 17px; line-height: 1.65; }
`;

const ProductLinks = styled.div`
  margin-top: 30px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;

  a { padding: 10px 14px; border: 1px solid rgba(255,255,255,.2); border-radius: 999px; color: #fff; background: rgba(255,255,255,.09); font-size: 12px; font-weight: 800; text-transform: uppercase; }
  a:hover { background: rgba(255,255,255,.16); }
`;

const Final = styled.section`
  padding: clamp(82px, 10vw, 138px) 24px;
  background: #181a17;
  color: #fff;
  text-align: center;

  h2 { max-width: 920px; margin: 0 auto; font-family: var(--font-heading, 'BebasNeuePro', sans-serif); font-size: clamp(58px, 7vw, 96px); line-height: .92; text-transform: uppercase; }
  p { max-width: 620px; margin: 24px auto 0; color: rgba(255,255,255,.68); font-size: 16px; line-height: 1.65; }
  ${Actions} { justify-content: center; }
`;

const Services = () => {
  usePageSEO({
    title: 'Продукты и возможности Lagom Life',
    description: 'Спортивное питание, продукты для ежедневного рациона и подборки экспертов Lagom Life. Собственное производство в Самарской области.'
  });

  return (
    <Page>
      <Hero>
        <HeroImage src="/services/hero-products.png" alt="Продукты Lagom Life и спортсменка после тренировки" />
        <HeroInner>
          <HeroCopy>
            <Eyebrow>Lagom Life · продукты и поддержка</Eyebrow>
            <h1>Спортивное питание и продукты на каждый день</h1>
            <p>Протеины, гейнеры, аминокислоты, витамины и травяные напитки. В карточках — состав, вкус, вес, способ приготовления и цена.</p>
            <Actions>
              <Button to="/catalog" $primary>Подобрать продукты <FiArrowRight aria-hidden="true" /></Button>
              <Button to="/experts">Познакомиться с экспертами</Button>
            </Actions>
          </HeroCopy>
        </HeroInner>
      </Hero>

      <ProofBar>
        <div><FiActivity aria-hidden="true" /><p><strong>Своё производство</strong><span>Самарская область</span></p></div>
        <div><FiShield aria-hidden="true" /><p><strong>Открытые документы</strong><span>Сертификаты на сайте</span></p></div>
        <div><FiUsers aria-hidden="true" /><p><strong>Профессиональная среда</strong><span>Тренеры и нутрициологи</span></p></div>
      </ProofBar>

      <Section>
        <SectionHead>
          <span>Линейка Lagom Life</span>
          <h2>Три направления продукции</h2>
          <p>Ассортимент объединяет спортивное питание, продукты для повседневного рациона и готовые комплексы от тренеров и нутрициологов.</p>
        </SectionHead>
        <DirectionGrid>
          {DIRECTIONS.map(item => {
            const Icon = item.icon;
            return (
              <DirectionCard key={item.number} $number={item.number} $tone={item.tone}>
                <Icon aria-hidden="true" />
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </DirectionCard>
            );
          })}
        </DirectionGrid>
      </Section>

      <Feature>
        <FeatureImage src="/services/production.png" alt="Контроль продукции Lagom Life на производстве" loading="lazy" />
        <FeatureCopy>
          <SectionHead>
            <span>Производим сами</span>
            <h2>Видим весь путь продукта</h2>
            <p>Спортивное питание выпускается в Самарской области. Мы контролируем не только упаковку, но и то, что происходит до неё.</p>
          </SectionHead>
          <Checklist>
            <div><FiCheckCircle aria-hidden="true" /><span>Проверка сырья перед производством</span></div>
            <div><FiCheckCircle aria-hidden="true" /><span>Контроль смешивания и фасовки</span></div>
            <div><FiCheckCircle aria-hidden="true" /><span>Проверка готовых партий и документов</span></div>
            <div><FiCheckCircle aria-hidden="true" /><span>Обратная связь от тренеров и профильных экспертов</span></div>
          </Checklist>
        </FeatureCopy>
      </Feature>

      <Taste>
        <TasteImage src="/home/protein-taste-v3.png" alt="Покупательница готовит коктейль с продуктами Lagom Life" loading="lazy" />
        <TasteInner>
          <TasteCopy>
            <Eyebrow>Вкусовая линейка</Eyebrow>
            <h2>От маффина и тирамису до фруктового микса</h2>
            <p>В каталоге — сывороточные, соевые, казеиновые и многокомпонентные протеины, гейнеры и функциональные напитки. Среди вкусов — маффин, тирамису, шоколадное мороженое, тропики, клубника и банан.</p>
            <ProductLinks>
              <Link to="/catalog">Протеины</Link>
              <Link to="/catalog">Гейнеры</Link>
              <Link to="/catalog">Аминокислоты</Link>
              <Link to="/catalog">Травяные напитки</Link>
            </ProductLinks>
          </TasteCopy>
        </TasteInner>
      </Taste>

      <Section>
        <SectionHead>
          <span>Экспертные подборки</span>
          <h2>Продуктовые комплексы от тренеров и нутрициологов</h2>
          <p>Каждая страница эксперта объединяет его опыт, материалы и готовые наборы продуктов под разные сценарии.</p>
        </SectionHead>
        <DirectionGrid>
          <DirectionCard $number="01" $tone="sage"><FiUsers aria-hidden="true" /><h3>Нутрициологи</h3><p>Помогают смотреть на продукт как на часть рациона, а не как на отдельную банку.</p></DirectionCard>
          <DirectionCard $number="02" $tone="clay"><FiZap aria-hidden="true" /><h3>Тренеры</h3><p>Знают, как питание сочетается с нагрузкой, восстановлением и спортивным режимом.</p></DirectionCard>
          <DirectionCard $number="03" $tone="dark"><FiHeart aria-hidden="true" /><h3>Практики</h3><p>Делятся личным опытом использования и понятными сценариями на каждый день.</p></DirectionCard>
        </DirectionGrid>
      </Section>

      <Final>
        <h2>Lagom Life — для тренировок и на каждый день</h2>
        <p>Спортивная линейка, витаминно-минеральные комплексы, травяные напитки и подборки профильных экспертов.</p>
        <Actions>
          <Button to="/catalog" $primary>Выбрать по цели <FiArrowRight aria-hidden="true" /></Button>
          <Button to="/experts">Смотреть экспертов</Button>
        </Actions>
      </Final>
    </Page>
  );
};

export default Services;
